import type { ReactNode } from 'react'
import { Link } from '@inertiajs/react'

import Badge from '@components/atoms/Badge'
import { PageLayout } from '@components/layouts/PageLayout'
import { CopyButton } from '@components/molecules/CopiableValue'
import { Table } from '@components/molecules/Table'

import { docsLayout } from '../components/DocsLayout'
import { findComponentDoc, TIER_LABEL } from '../docs/registry'
import type { AnyComponentDoc } from '../docs/types'

type ExampleData = AnyComponentDoc['examples'][number]

type Props = { component: string; indexHref: string }

function Needed({ children }: { children: ReactNode }) {
  return (
    <span className="tw:inline-block tw:rounded-lg tw:border tw:border-dashed tw:border-warning-300 tw:bg-warning-25 tw:px-2 tw:py-1 tw:text-sm tw:text-warning-700">
      {children}
    </span>
  )
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="tw:flex tw:flex-col tw:gap-3">
      <h2 className="tw:text-base tw:font-semibold tw:text-gray-cool-900">{title}</h2>
      {children}
    </section>
  )
}

export function CodeBlock({ code }: { code: string }) {
  return (
    <div className="tw:relative tw:rounded-xl tw:bg-gray-cool-900 tw:px-5 tw:py-4">
      <pre className="tw:m-0 tw:overflow-x-auto tw:font-mono tw:text-sm tw:leading-5 tw:text-gray-cool-200">{code}</pre>
      <span className="tw:absolute tw:right-3 tw:top-3 tw:rounded-md tw:bg-white/10 tw:px-2 tw:py-1 tw:[&_button]:text-gray-cool-200 tw:[&_button:hover]:text-white">
        <CopyButton value={code} testId="code" label="Copy code" />
      </span>
    </div>
  )
}

function toJsx(name: string, variant: Record<string, unknown>): string {
  const { children, ...rest } = variant
  const attrs = Object.entries(rest).map(([key, value]) => {
    if (value === true) return key
    if (typeof value === 'string') return `${key}="${value}"`
    return `${key}={${JSON.stringify(value)}}`
  })
  const open = [name, ...attrs].join(' ')
  return children === undefined ? `<${open} />` : `<${open}>${children}</${name}>`
}

function BulletList({ lines }: { lines: string[] }) {
  return (
    <ul className="tw:flex tw:list-disc tw:flex-col tw:gap-1 tw:pl-4 tw:text-sm tw:text-gray-cool-700">
      {lines.map((line) => (
        <li key={line}>{line}</li>
      ))}
    </ul>
  )
}

function Example({ doc, example }: { doc: AnyComponentDoc; example: ExampleData }) {
  const Component = doc.component
  const variants =
    'render' in example ? [] : example.variants.map((variant) => ({ variant, code: toJsx(doc.name, variant) }))

  return (
    <div className="tw:flex tw:flex-col tw:gap-2">
      <h3 className="tw:text-sm tw:font-semibold tw:text-gray-cool-700">{example.title}</h3>
      <div
        className="tw:flex tw:flex-wrap tw:items-center tw:gap-4 tw:rounded-xl tw:border tw:border-gray-cool-200 tw:bg-gray-cool-50 tw:p-6"
        style={{ backgroundImage: 'radial-gradient(#DCDFEA 1px, transparent 1px)', backgroundSize: '16px 16px' }}
      >
        {'render' in example
          ? example.render()
          : Component && variants.map(({ variant, code }) => <Component key={code} {...variant} />)}
      </div>
      {example.note && <p className="tw:text-sm tw:text-gray-cool-600">{example.note}</p>}
      <CodeBlock code={'render' in example ? example.code : variants.map(({ code }) => code).join('\n')} />
    </div>
  )
}

function Guidance({ heading, items }: { heading: string; items: string[] }) {
  return (
    <div className="tw:flex tw:flex-1 tw:flex-col tw:gap-2 tw:rounded-xl tw:border tw:border-gray-cool-200 tw:p-4">
      <h3 className="tw:text-sm tw:font-semibold tw:text-gray-cool-900">{heading}</h3>
      <BulletList lines={items} />
    </div>
  )
}

function PropsTable({ props }: { props: NonNullable<AnyComponentDoc['props']> }) {
  return (
    <div className="tw:overflow-hidden tw:rounded-xl tw:border tw:border-gray-cool-200">
      <Table.Root>
        <Table.Header className="tw:bg-gray-cool-50">
          <Table.Row>
            <Table.Head>Name</Table.Head>
            <Table.Head>Type</Table.Head>
            <Table.Head>Default</Table.Head>
            <Table.Head>Description</Table.Head>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {Object.entries(props).map(([name, prop]) => (
            <Table.Row key={name}>
              <Table.Cell className="tw:whitespace-nowrap tw:align-top tw:font-mono tw:text-xs tw:text-gray-cool-900">
                {name}
                {prop.required && <span className="tw:ml-1 tw:text-error-600">*</span>}
              </Table.Cell>
              <Table.Cell className="tw:align-top tw:font-mono tw:text-xs tw:text-primary-700">{prop.type}</Table.Cell>
              <Table.Cell className="tw:align-top tw:font-mono tw:text-xs tw:text-gray-cool-600">
                {prop.default ?? '-'}
              </Table.Cell>
              <Table.Cell className="tw:align-top tw:text-sm tw:text-gray-cool-700">
                {prop.description ?? <Needed>Needs a description</Needed>}
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table.Root>
    </div>
  )
}

function NotFound({ component, indexHref }: Props) {
  return (
    <>
      <PageLayout.Header heading="Component not documented" />
      <PageLayout.Body>
        <p className="tw:text-sm tw:text-gray-cool-600">
          There is no docs entry for <code className="tw:font-mono">{component}</code> yet. Add one under{' '}
          <code className="tw:font-mono">app/frontend/contexts/design_system/docs/</code>.
        </p>
        <Link href={indexHref} className="tw:mt-4 tw:inline-block tw:text-sm tw:font-medium tw:text-primary-700">
          Back to design docs
        </Link>
      </PageLayout.Body>
    </>
  )
}

const ComponentDoc = ({ component, indexHref }: Props) => {
  const doc = findComponentDoc(component)
  if (!doc) return <NotFound component={component} indexHref={indexHref} />

  return (
    <>
      <PageLayout.Header heading={doc.name} />
      <PageLayout.Body>
        <div className="tw:flex tw:max-w-5xl tw:flex-col tw:gap-8 tw:pb-16">
          <header className="tw:flex tw:flex-col tw:gap-3">
            <div className="tw:flex tw:items-center tw:gap-2">
              <Badge color="primary">{TIER_LABEL[doc.tier].replace(/s$/, '')}</Badge>
            </div>
            {doc.description ? (
              <p className="tw:max-w-prose tw:text-sm tw:text-gray-cool-600">{doc.description}</p>
            ) : (
              <Needed>Needs a description: what this component is for, and when to reach for it.</Needed>
            )}
            <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-3 tw:text-xs">
              <span className="tw:inline-flex tw:items-center tw:gap-2 tw:rounded-md tw:bg-gray-cool-100 tw:px-2 tw:py-1 tw:font-mono tw:text-gray-cool-700">
                {doc.importStatement}
                <CopyButton value={doc.importStatement} testId="import" label="Copy import" />
              </span>
              <span className="tw:font-mono tw:text-gray-cool-400">{doc.sourcePath}</span>
            </div>
          </header>

          <Section title="Examples">
            <div className="tw:flex tw:flex-col tw:gap-6">
              {doc.examples.map((example) => (
                <Example key={example.title} doc={doc} example={example} />
              ))}
            </div>
          </Section>

          {doc.props && (
            <Section title="Props">
              <PropsTable props={doc.props} />
            </Section>
          )}

          <Section title="Usage">
            {doc.usage ? (
              <div className="tw:flex tw:flex-col tw:gap-4 tw:md:flex-row">
                <Guidance heading="Use this when" items={doc.usage.do} />
                <Guidance heading={"Don't use this for"} items={doc.usage.dont} />
              </div>
            ) : (
              <Needed>Needs do/don&apos;t guidance from whoever owns this component.</Needed>
            )}
          </Section>

          {doc.choosing && <Section title={doc.choosing.title}>{doc.choosing.content}</Section>}

          <Section title="Accessibility">
            {doc.a11y?.length ? <BulletList lines={doc.a11y} /> : <Needed>Needs an accessibility review.</Needed>}
          </Section>
        </div>
      </PageLayout.Body>
    </>
  )
}

ComponentDoc.layout = docsLayout

export default ComponentDoc
